import * as THREE from 'three';
import proj4 from 'proj4';

const UTM = '+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs +type=crs';
const WGS = 'EPSG:4326';

/** Radius of the tile window loaded around the current view (tiles). */
const MAX_TILE_RADIUS = 8;
const TILE_URL = (z: number, x: number, y: number) => `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;

function lonLatToTileX(lon: number, z: number) {
  return Math.floor(((lon + 180) / 360) * Math.pow(2, z));
}
function lonLatToTileY(lat: number, z: number) {
  const r = (lat * Math.PI) / 180;
  return Math.floor(((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * Math.pow(2, z));
}
function tileXToLon(x: number, z: number) {
  return (x / Math.pow(2, z)) * 360 - 180;
}
function tileYToLat(y: number, z: number) {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, z);
  return (Math.atan(Math.sinh(n)) * 180) / Math.PI;
}

/** Choose an OSM zoom level from the camera-to-target distance (LOD). */
function zoomForDistance(dist: number): number {
  if (dist > 22000) return 11;
  if (dist > 11000) return 12;
  if (dist > 5500) return 13;
  if (dist > 2200) return 14;
  if (dist > 900) return 15;
  return 16;
}

export class BasemapLayer {
  readonly group = new THREE.Group();
  private project: (lon: number, lat: number) => [number, number];
  private originUtm: [number, number];
  private loader = new THREE.TextureLoader();
  private tiles = new Map<string, THREE.Mesh>();
  private loading = new Set<string>();
  private zoom = -1;
  private enabled = false;

  constructor(originLon: number, originLat: number) {
    this.group.name = 'basemap';
    this.group.visible = false;
    this.group.renderOrder = -3;
    this.originUtm = proj4(WGS, UTM, [originLon, originLat]) as [number, number];
    this.project = (lon, lat) => {
      const p = proj4(WGS, UTM, [lon, lat]);
      return [p[0] - this.originUtm[0], -(p[1] - this.originUtm[1])];
    };
    this.loader.setCrossOrigin('anonymous');
  }

  setVisible(v: boolean) {
    this.enabled = v;
    this.group.visible = v;
    if (!v) {
      this.zoom = -1;
    }
  }

  get available() {
    return true;
  }

  /** Called every frame; loads/removes tiles so the whole viewport is covered. */
  update(targetX: number, targetZ: number, camera: THREE.PerspectiveCamera) {
    if (!this.enabled) return;
    const dist = camera.position.distanceTo(new THREE.Vector3(targetX, 0, targetZ));
    const z = zoomForDistance(dist);
    const [lon, lat] = this.unproject(targetX, targetZ);
    const cx = lonLatToTileX(lon, z);
    const cy = lonLatToTileY(lat, z);

    if (z !== this.zoom) {
      for (const m of this.tiles.values()) this.disposeTile(m);
      this.tiles.clear();
      this.zoom = z;
    }

    // Determine which tiles are visible on screen: project the four frustum
    // corners onto the ground plane, then take the bounding box in tile space.
    let minTx = Infinity;
    let maxTx = -Infinity;
    let minTy = Infinity;
    let maxTy = -Infinity;
    for (const [x, y] of this.groundFrustumCorners(camera)) {
      const [clon, clat] = this.unproject(x, y);
      const tx = lonLatToTileX(clon, z);
      const ty = lonLatToTileY(clat, z);
      minTx = Math.min(minTx, tx);
      maxTx = Math.max(maxTx, tx);
      minTy = Math.min(minTy, ty);
      maxTy = Math.max(maxTy, ty);
    }
    // margin + clamp so the window stays bounded (e.g. near the horizon)
    minTx = Math.max(minTx - 1, cx - MAX_TILE_RADIUS);
    maxTx = Math.min(maxTx + 1, cx + MAX_TILE_RADIUS);
    minTy = Math.max(minTy - 1, cy - MAX_TILE_RADIUS);
    maxTy = Math.min(maxTy + 1, cy + MAX_TILE_RADIUS);

    const wanted = new Set<string>();
    for (let x = minTx; x <= maxTx; x++) {
      for (let y = minTy; y <= maxTy; y++) {
        const key = `${z}/${x}/${y}`;
        wanted.add(key);
        if (!this.tiles.has(key)) this.loadTile(z, x, y, key);
      }
    }
    for (const [key, mesh] of this.tiles) {
      if (!wanted.has(key)) {
        this.disposeTile(mesh);
        this.tiles.delete(key);
      }
    }
  }

  /** World-space ground points where the four viewport corner rays hit y=0. */
  private groundFrustumCorners(camera: THREE.PerspectiveCamera): [number, number][] {
    const pos = camera.getWorldPosition(new THREE.Vector3());
    const fwd = camera.getWorldDirection(new THREE.Vector3());
    const right = new THREE.Vector3().crossVectors(fwd, camera.up).normalize();
    const up = new THREE.Vector3().crossVectors(right, fwd).normalize();
    const halfH = Math.tan(((camera.fov * Math.PI) / 180) / 2);
    const halfW = halfH * camera.aspect;

    const out: [number, number][] = [];
    for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
      const dir = new THREE.Vector3()
        .copy(fwd)
        .addScaledVector(right, sx * halfW)
        .addScaledVector(up, sy * halfH)
        .normalize();
      if (Math.abs(dir.y) > 1e-6) {
        const t = -pos.y / dir.y;
        if (t > 0) {
          const p = pos.clone().addScaledVector(dir, t);
          out.push([p.x, p.z]);
        }
      }
    }
    return out;
  }

  private unproject(x: number, z: number): [number, number] {
    const p = proj4(UTM, WGS, [x + this.originUtm[0], -z + this.originUtm[1]]);
    return [p[0], p[1]];
  }

  private loadTile(z: number, x: number, y: number, key: string) {
    if (this.loading.has(key) || this.tiles.has(key)) return;
    this.loading.add(key);
    this.loader.load(
      TILE_URL(z, x, y),
      (tex) => {
        this.loading.delete(key);
        if (!this.enabled || this.zoom !== z) {
          tex.dispose();
          return;
        }
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.anisotropy = 4;
        const mesh = this.makeTileMesh(z, x, y, tex);
        this.tiles.set(key, mesh);
        this.group.add(mesh);
      },
      undefined,
      () => {
        this.loading.delete(key);
      },
    );
  }

  private makeTileMesh(z: number, x: number, y: number, tex: THREE.Texture): THREE.Mesh {
    const lon0 = tileXToLon(x, z);
    const lon1 = tileXToLon(x + 1, z);
    const lat0 = tileYToLat(y, z);
    const lat1 = tileYToLat(y + 1, z);
    const nw = this.project(lon0, lat0);
    const ne = this.project(lon1, lat0);
    const se = this.project(lon1, lat1);
    const sw = this.project(lon0, lat1);

    const geo = new THREE.BufferGeometry();
    const y0 = -0.6;
    geo.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(
        [nw[0], y0, nw[1], ne[0], y0, ne[1], se[0], y0, se[1], sw[0], y0, sw[1]],
        3,
      ),
    );
    // texture image top-left is NW; three UV (0,0) is bottom-left
    geo.setAttribute(
      'uv',
      new THREE.Float32BufferAttribute([0, 1, 1, 1, 1, 0, 0, 0], 2),
    );
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: false,
      depthWrite: true,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.renderOrder = -3;
    return mesh;
  }

  private disposeTile(mesh: THREE.Mesh) {
    this.group.remove(mesh);
    mesh.geometry.dispose();
    const mat = mesh.material as THREE.MeshBasicMaterial;
    mat.map?.dispose();
    mat.dispose();
  }
}
